import { useState, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { Transaction } from '@/hooks/useTransactions';
import { useHistoricalExchangeRate } from '@/hooks/useHistoricalExchangeRate';
import { format } from 'date-fns';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TransactionListItemProps {
  transaction: Transaction;
  displayCurrency?: string;
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
  onClick?: (transaction: Transaction) => void;
}

export function TransactionListItem({
  transaction,
  displayCurrency,
  onEdit,
  onDelete,
  onClick,
}: TransactionListItemProps) {
  const needsConversion = displayCurrency && displayCurrency !== transaction.currency;
  const transactionDate = transaction.transaction_date.split('T')[0]; // Extract date part
  
  const { convert, isLoading, error } = useHistoricalExchangeRate(
    needsConversion ? transactionDate : '',
    needsConversion ? transaction.currency : 'USD'
  );

  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  // Show loading indicator only if conversion takes >500ms
  useEffect(() => {
    if (isLoading && needsConversion) {
      const timer = setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 500);

      return () => {
        clearTimeout(timer);
        setShowLoadingIndicator(false);
      };
    } else {
      setShowLoadingIndicator(false);
    }
  }, [isLoading, needsConversion]);

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const renderAmount = () => {
    const nativeAmount = Number(transaction.amount);
    const nativeFormatted = formatCurrency(nativeAmount, transaction.currency);

    // No conversion needed
    if (!needsConversion) {
      return (
        <span className={`inline-flex items-center gap-1 ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
          {transaction.type === 'income' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {nativeFormatted}
        </span>
      );
    }

    // Show loading indicator if conversion is taking time
    if (showLoadingIndicator) {
      return (
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Converting...
        </span>
      );
    }

    // Show error and native amount only if conversion fails
    if (error) {
      return (
        <div className="space-y-1">
          <span className={`inline-flex items-center gap-1 ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
            {transaction.type === 'income' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {nativeFormatted}
          </span>
          <p className="text-xs text-destructive">Conversion unavailable</p>
        </div>
      );
    }

    // Show converted amount with native amount in parentheses
    try {
      const convertedAmount = convert(nativeAmount, transaction.currency, displayCurrency);
      const convertedFormatted = formatCurrency(convertedAmount, displayCurrency!);

      return (
        <span className={`inline-flex items-center gap-1 ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
          {transaction.type === 'income' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          <span>
            {convertedFormatted} <span className="text-xs text-muted-foreground">({nativeFormatted})</span>
          </span>
        </span>
      );
    } catch (err) {
      // Fallback to native amount on conversion error
      return (
        <span className={`inline-flex items-center gap-1 ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
          {transaction.type === 'income' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {nativeFormatted}
        </span>
      );
    }
  };

  return (
    <TableRow
      className={onClick ? 'cursor-pointer' : undefined}
      onClick={onClick ? () => onClick(transaction) : undefined}
    >
      <TableCell className="text-muted-foreground">
        {format(new Date(transaction.transaction_date), 'MMM d, yyyy')}
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <span>{transaction.description || '—'}</span>
        </div>
      </TableCell>
      <TableCell>
        {transaction.sub_categories ? (
          <span>{transaction.sub_categories.icon} {transaction.sub_categories.name}</span>
        ) : transaction.categories ? (
          <span>{transaction.categories.icon} {transaction.categories.name}</span>
        ) : (
          <span className="text-muted-foreground">Uncategorized</span>
        )}
      </TableCell>
      <TableCell>{transaction.accounts?.name ?? '—'}</TableCell>
      <TableCell className="text-right font-mono">
        {renderAmount()}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(transaction)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(transaction.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
